import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { ENUM__page_type } from '@/enums/ENUM__page_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_ui, use_slice_user, use_slice_workspace, use_slice_workspace_billing } from '@/hooks/store/use_slices';
import { use_workspace_balance } from '@/hooks/use_workspace_balance';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ESettingsWorkspaceTab } from '@/pages/+settings/workspace/ESettingsWorkspaceTab';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { LOCAL__ui_set_string } from '@/state/ui/_base/LOCAL__ui_set_string';
import { set_timeout } from '@/utils/utils_client';
import { string_to_span } from '@/utils/utils_html';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { format_compact_number } from '@shared/utils/utils_number';
import { format_time_with_unit, get_time_before_utc_midnight } from '@shared/utils/utils_time';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UI_PRIMITIVE__tooltip } from '../../../components/ui/UI_PRIMITIVE__tooltip';

export function LABEL__workspace_balance() {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const dispatch = use_dispatch();
  const navigate = useNavigate();

  const workspace_balance = use_workspace_balance();

  const { user_id } = use_slice_user(['user_id']);
  const { balances_map } = use_slice_workspace_billing(['balances_map']);
  const { workspace_current } = use_slice_workspace(['workspace_current']);
  const { global_need_show_first_animation } = use_slice_ui(['global_need_show_first_animation']);

  const [value, set_value] = useState(workspace_balance);
  const [previous_workspace_id, set_previous_workspace_id] = useState(workspace_current._id);
  const [is_value_decreased, set_is_value_decreased] = useState(false);

  const is_user_owner = useMemo(() => workspace_current.user_id_owner === user_id, [workspace_current, user_id]);

  /*
   * EFFECTS
   */

  use_effect(() => {
    set_previous_workspace_id(workspace_current._id);

    set_value(workspace_balance);

    if (workspace_balance < value && previous_workspace_id === workspace_current._id) {
      set_is_value_decreased(true);
    }

    const timeout = set_timeout(() => set_is_value_decreased(false), CONSTANTS_TIME__client.TIMEOUT_DURATION_ANIMATION_CREDITS_BALANCE);

    return () => clearTimeout(timeout);
  }, [workspace_balance, workspace_current._id]);

  /*
   * EVENTS
   */

  async function onClick() {
    if (workspace_current.user_id_owner != user_id) return;

    dispatch(
      LOCAL__ui_set_string({
        key: 'page_settings_workspace_current_tab',
        value: ESettingsWorkspaceTab.CREDITS_TOP_UP,
        valid_values: Object.values(ESettingsWorkspaceTab),
      }),
    );

    await navigate(ENUM__page_type.SETTINGS_WORKSPACE);
  }

  function get_tooltip_title_restore_credits() {
    let out = '';

    if (!workspace_current.is_personal) return out;

    let balance = balances_map.get_or_assert(workspace_current._id);

    if (balance.credits >= CONSTANTS__shared.CREDITS_GUARANTEED_PER_DAY_PERSONAL) return out;

    let time_left_before_restore = get_time_before_utc_midnight();

    out = t(i18n_base_ui.sidebar_navigation.workspace_credits.tooltip.title_restore, {
      count: CONSTANTS__shared.CREDITS_GUARANTEED_PER_DAY_PERSONAL,
      time: format_time_with_unit(time_left_before_restore),
    });

    return out;
  }

  function get_tooltip_title() {
    let out = t(i18n_base_ui.sidebar_navigation.workspace_credits.tooltip.title, { balance: workspace_balance.format_with_spaces() });

    if (workspace_current.user_id_owner == user_id) {
      out = t(i18n_base_ui.sidebar_navigation.workspace_credits.tooltip.title_with_description, {
        balance: workspace_balance.format_with_spaces(),
      });
    }

    out += get_tooltip_title_restore_credits();

    return out;
  }

  /*
   * METHODS
   */

  function get_color() {
    let out = '';

    if (value === 0) {
      out = 'text-danger font-bold';
    } else {
      out = 'text-purchase';
      out += is_user_owner ? ' hover:!text-purchase-hover' : '';
    }

    return out;
  }

  /*
   * RENDERS
   */
  function render_view() {
    let out = <></>;

    let color = get_color();

    if (is_user_owner) {
      out = (
        <UI_PRIMITIVE__button {...{ onClick }} variant={'no-border-on-bar'} size={'size-full'} className={`${color} text-sm font-medium`}>
          {format_compact_number(value)}
        </UI_PRIMITIVE__button>
      );
    } else {
      out = <div className={`max-h-[24px] text-lg flex flex-center ${color}`}>{format_compact_number(value)}</div>;
    }

    return out;
  }

  /*
   * HTML
   */

  if (isNaN(value)) return null;

  return (
    <UI_PRIMITIVE__tooltip tooltip_title={string_to_span(get_tooltip_title())} side="right" delay_duration={0}>
      <div
        className={`w-16 h-full flex flex-center hover-animation 
          ${is_value_decreased ? 'opacity-50 scale-90' : 'opacity-100 scale-100'} 
          ${global_need_show_first_animation ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}
      >
        {render_view()}
      </div>
    </UI_PRIMITIVE__tooltip>
  );
}
